import { createContext, useContext, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { ApplicationCode } from '@shared/config/applications';
import type { NavItem } from '@shared/components/layout/nav-config';

/** One titled block of the sidebar. */
export interface ShellNavSection {
  /** Section heading; omitted for the first, untitled block. */
  heading?: string;
  items: NavItem[];
}

/** An extra entry in the account menu (e.g. "My profile"). */
export interface ShellMenuLink {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Shown only when the user holds this permission. */
  permission?: string;
}

/**
 * What makes one HEXAUNITY application different from another. Everything
 * else — session, guards, shell, design system — is shared.
 */
export interface AppConfig {
  /** The application code identity-service grants (`user_application_access`). */
  application: ApplicationCode;
  /** Display name: "HRMS", "Finance", "Employee Portal". */
  name: string;
  /** One line under the name on the sign-in screen. */
  tagline: string;
  navigation: ShellNavSection[];
  userMenu?: ShellMenuLink[];
  /**
   * Routes whose table fills the viewport and scrolls internally (the shell
   * locks to the viewport height there instead of growing with the page).
   */
  fullHeightRoutes?: readonly string[];
  /**
   * Whether the application gates screens on permission keys. The Employee
   * Portal does not: its access is the fixed self-service set the backend
   * applies for EMPLOYEE_PORTAL (no role permission is consulted there).
   */
  usesPermissions: boolean;
}

const AppConfigContext = createContext<AppConfig | null>(null);

export function AppConfigProvider({ config, children }: { config: AppConfig; children: ReactNode }) {
  return <AppConfigContext.Provider value={config}>{children}</AppConfigContext.Provider>;
}

export function useAppConfig(): AppConfig {
  const config = useContext(AppConfigContext);
  if (!config) throw new Error('useAppConfig must be used inside <AppConfigProvider>.');
  return config;
}
